import React from 'react';
import SegmentedControl from '../common/SegmentedControl';

/**
 * PaymentAnalyticsFilters
 * Range + custom-date controls. Two layouts share the same UI:
 *   - inline: horizontal pill group (desktop header)
 *   - sheet: stacked pills with larger hit targets (mobile BottomSheet)
 *
 * Props:
 *   value: { rangePreset, customStart, customEnd }
 *   onChange: (partial) => void — merges into state in the parent
 *   ranges: segment options passed straight to SegmentedControl
 *   layout: 'inline' | 'sheet'
 *   onApply?: () => void — shown as a sticky Apply button in sheet layout
 */
export default function PaymentAnalyticsFilters({
  value,
  onChange,
  ranges,
  layout = 'inline',
  onApply
}) {
  const { rangePreset, customStart, customEnd } = value;
  const isSheet = layout === 'sheet';

  return (
    <div className={isSheet ? 'flex flex-col' : 'flex flex-col md:flex-row md:items-center gap-2 w-full md:w-auto'}>
      <div className={isSheet ? 'p-4 space-y-4' : 'contents'}>
        {isSheet && <div className="text-xs font-medium text-gray-500 uppercase">Date range</div>}
        <div className={isSheet ? 'flex flex-wrap gap-1.5' : ''}>
          {isSheet ? (
            ranges.map(opt => {
              const active = opt.value === rangePreset;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => onChange({ rangePreset: opt.value })}
                  className={[
                    'text-sm px-3 py-2 rounded-full border transition-colors',
                    active
                      ? 'bg-gray-900 text-white border-gray-900'
                      : 'bg-white text-gray-700 border-gray-300 hover:border-gray-400'
                  ].join(' ')}
                >
                  {opt.label}
                </button>
              );
            })
          ) : (
            <SegmentedControl
              options={ranges}
              value={rangePreset}
              onChange={v => onChange({ rangePreset: v })}
              ariaLabel="Date range"
            />
          )}
        </div>
        {rangePreset === 'custom' && (
          <div className={isSheet ? 'space-y-2' : 'flex flex-wrap gap-2 w-full md:w-auto'}>
            {isSheet && <div className="text-xs font-medium text-gray-500 uppercase">Custom dates</div>}
            <div className={isSheet ? 'grid grid-cols-2 gap-2' : 'contents'}>
              <input
                type="date"
                aria-label="Start date"
                value={customStart || ''}
                onChange={e => onChange({ customStart: e.target.value })}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm min-w-0"
              />
              <input
                type="date"
                aria-label="End date"
                value={customEnd || ''}
                onChange={e => onChange({ customEnd: e.target.value })}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm min-w-0"
              />
            </div>
          </div>
        )}
      </div>
      {isSheet && onApply && (
        <div className="sticky bottom-0 bg-white border-t border-gray-100 p-3">
          <button
            type="button"
            onClick={onApply}
            className="w-full bg-gray-900 text-white rounded-md py-2.5 text-sm font-medium hover:bg-gray-800"
          >
            Apply
          </button>
        </div>
      )}
    </div>
  );
}
